import React, { createContext, useContext, useMemo } from 'react';
import { CollapsibleCodeBlock } from '@/features/ai-agents/chat/code-generation-blocks';
import { CustomEventData } from '@/features/ai-agents/shared/models';

export const parseCustomEventPayload = (
  raw: unknown,
): CustomEventData | null => {
  try {
    const parsed = typeof raw === `string` ? JSON.parse(raw) : raw;
    if (typeof parsed?.content !== `string`) return null;
    return parsed as CustomEventData;
  } catch {
    return null;
  }
};

export const extractCustomEventKind = (className?: string): string | null => {
  if (!className) return null;
  const prefix = `language-vsly/json/custom-event/`;
  const match = className.split(` `).find((cls) => cls.startsWith(prefix));
  if (!match) return null;
  return match.slice(prefix.length) || null;
};

const CUSTOM_EVENT_FENCE_REGEX =
  /```vsly\/json\/custom-event\/[^\n]*\n([\s\S]*?)```/g;

export const buildLatestEventsByKey = (
  markdown: string,
): Map<string, string> => {
  const map = new Map<string, string>();
  const regex = new RegExp(CUSTOM_EVENT_FENCE_REGEX.source, `g`);
  let match = regex.exec(markdown);
  while (match !== null) {
    const rawContent = match[1].trim();
    const payload = parseCustomEventPayload(rawContent);
    if (payload?.event_key) {
      map.set(payload.event_key, rawContent);
    }
    match = regex.exec(markdown);
  }
  return map;
};

const CustomEventLatestContext = createContext<Map<string, string>>(new Map());

export const CustomEventLatestProvider = ({
  markdown,
  children,
}: {
  markdown: string;
  children: React.ReactNode;
}) => {
  const latestByKey = useMemo(
    () => buildLatestEventsByKey(markdown),
    [markdown],
  );
  return (
    <CustomEventLatestContext.Provider value={latestByKey}>
      {children}
    </CustomEventLatestContext.Provider>
  );
};

export const CustomEventBlock = ({
  kind,
  children,
  isLatestMessage: _isLatestMessage,
}: {
  kind: string;
  children: unknown;
  isLatestMessage: boolean;
}) => {
  const raw = Array.isArray(children) ? children[0] : children;
  const rawStr = typeof raw === `string` ? raw.trim() : null;
  const payload = parseCustomEventPayload(raw);
  const latestByKey = useContext(CustomEventLatestContext);

  if (!payload) return null;

  const latestRaw = latestByKey.get(payload.event_key);
  const isLatestForKey = !latestRaw || latestRaw === rawStr;

  if (!isLatestForKey) return null;

  if (kind === `PROGRESS`) {
    return (
      <CollapsibleCodeBlock
        code={payload.more_content}
        isGenerating={payload.loading}
        language="json"
        labelOverride={payload.content}
      />
    );
  }

  return null;
};
